// @ts-nocheck — thin route wrapper, types added when ForecastItaly is genericized
import React from "react";
import { Link, useParams } from "react-router-dom";
import { Dashboard as ForecastMarket } from "./ForecastMarket";
import ViewerChip from "../components/composed/ViewerChip";
import { getMarketConfig } from "../lib/markets-config";
// Phase 4e — IT/EN parity.
import { useLang } from "../lib/lang";
// Phase B1 — layout primitive.
import Page from "../components/ui/Page";

/* src/views/MarketDashboard.tsx — Phase 7 sub-step 1, updated by 7.5a.

   Looks up the requested market in src/lib/markets-config.ts. If it's
   registered, renders the country dashboard (which is still
   ForecastItaly today — sub-step 7.5b threads the config through its
   internals so the title / fiscal year / country filter can change
   per market).

   For unregistered slugs we render a clear "not yet onboarded"
   surface — never silently fall back to Italy. Marco's multi-market
   data model (5.5c) depends on country attribution being honest.

   Side-effect: we expose the active config on window.MARKET_CONFIG so
   legacy code in ForecastItaly can pick it up incrementally without a
   wholesale refactor. Cleaned up once 7.5b is done.
*/

export default function MarketDashboard() {
  const { marketId } = useParams<{ marketId: string }>();
  const slug = (marketId || "").toLowerCase();
  const config = getMarketConfig(slug);
  const lang = useLang();

  // Bridge for ForecastMarket (was ForecastItaly until Phase H): the file
  // is no longer Italy-specific, but it still reads window.MARKET_CONFIG
  // at render time rather than via a proper React context. Setting it
  // here before render means any code path inside the dashboard that
  // wants market-aware behavior can pick it up via window today; a
  // future cleanup may move this to a typed context.
  React.useEffect(() => {
    if (typeof window !== "undefined") {
      (window as any).MARKET_CONFIG = config;
    }
    return () => {
      if (typeof window !== "undefined") {
        (window as any).MARKET_CONFIG = null;
      }
    };
  }, [config]);

  if (config) {
    return <ForecastMarket />;
  }

  return (
    <Page width="compact">
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "var(--space-5)", gap: "var(--space-3)" }}>
        <h1 style={{ margin: 0, fontSize: "var(--fs-h1)" }}>
          {lang === "it" ? "Mercato non ancora supportato" : "Market not yet supported"}
        </h1>
        <ViewerChip />
      </header>

      <p style={{ color: "var(--text-muted)", marginTop: 0, marginBottom: "var(--space-4)" }}>
        {lang === "it" ? "La dashboard per" : "The dashboard for"}
        <code style={{ margin: "0 var(--space-1)", padding: "0 var(--space-1)", background: "var(--bg-2)", borderRadius: 4, fontFamily: "var(--font-mono)" }}>
          {marketId}
        </code>
        {lang === "it"
          ? " non è ancora collegata. L'Italia è attualmente l'unico mercato con una vista forecast attiva."
          : " isn't wired up yet. Italy is currently the only market with a live forecast view."}
      </p>

      <p style={{ color: "var(--text-muted)", marginTop: 0, marginBottom: "var(--space-4)", fontSize: "var(--fs-body-sm)" }}>
        {lang === "it"
          ? "Aggiungere un nuovo mercato è un sub-step della Phase 7 (parametrizzare ForecastItaly per mercato, poi onboardare il prossimo paese)."
          : "Adding a new market is a sub-step in Phase 7 (parameterize ForecastItaly by market, then onboard the next country)."}
      </p>

      <Link to="/market/italy" style={{ color: "var(--accent)", textDecoration: "none", fontSize: "var(--fs-body)" }}>
        {lang === "it" ? "→ Previsioni Italia" : "→ Italy forecast"}
      </Link>
    </Page>
  );
}
